import Links from "@/shared/constants/navegacao"
import { Link } from "react-router-dom"
import { PersonProfilesFormMethods } from "./profile.types"
import { useState, useEffect } from "react"
import { LotacaoService } from "@/modules/business/lotacoes/lotacao.service"
import { ReadLotacaoDto } from "@/api.types"
import { SearchDtoLotacoes } from "@/shared/types/search.dto"
import { DataAmigavel } from "@/shared/utils/datas"
import { TipoLotacaoList } from "@/shared/constants/enumlist"
import { CategoriaDefensorList } from "@/modules/people/enums/categoria-defensor.enum"

interface DefensorProfileProps {
  methods: PersonProfilesFormMethods
  podeVerDefensor: boolean
}

export function DefensorProfile({ methods, podeVerDefensor }: DefensorProfileProps) {
  const defensorAtivo = methods.watch('defensor.ativo')
  const defensorId = methods.watch('defensor.id')
  const [lotacoesAtuais, setLotacoesAtuais] = useState<ReadLotacaoDto[]>([])

  const existeRegistro = !!defensorId
  const deveMostrarCampos = defensorAtivo || existeRegistro
  const camposDesabilitados = existeRegistro && !defensorAtivo

  useEffect(() => {
    if (defensorId) {
      const lotacaoService = new LotacaoService();
      const searchParams: SearchDtoLotacoes = {
        skip: 0,
        take: 9000,
        id_defensor: defensorId,
      } as SearchDtoLotacoes;

      lotacaoService
        .Enumerate(searchParams)
        .then((result) => {
          setLotacoesAtuais(result[0]);
        })
        .catch((error) => {
          console.warn('Erro ao carregar lotações:', error);
        });
    }
  }, [defensorId]);

  if (!podeVerDefensor) {
    return null
  }

  return (
    <div className="card mb-3">
      <div className="card-header">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="defensor_ativo"
            {...methods.register("defensor.ativo")}
          />
          <label className="form-check-label" htmlFor="defensor_ativo">
            Habilitar Perfil de Defensor(a)
          </label>
          <div className="float-end">
            <Link
              className="link-secondary me-3"
              to={`${Links.Vinculos_Index}?tipo=defensor`}
              target="_blank"
            >
              Vínculos
            </Link>
            <Link
              className="link-secondary"
              to={`${Links.Defensor_Timeline}?id=${methods.getValues('defensor.id') ?? ''}`}
              target="_blank"
            >
              Histórico
            </Link>
          </div>
        </div>
      </div>

      {deveMostrarCampos && (
        <div className="card-body row">
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-3">
                <label htmlFor="defensor_titulo" className="form-label">Título:</label>
                <input
                  id="defensor_titulo"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('defensor.titulo')}
                />
              </div>

              <div className="col-md-4">
                <label htmlFor="defensor_oab" className="form-label">Número OAB:</label>
                <input
                  id="defensor_oab"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('defensor.numero_oab')}
                />
              </div>

              <div className="col-md-5">
                <label htmlFor="defensor_matricula" className="form-label">Matrícula:</label>
                <input
                  id="defensor_matricula"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('defensor.matricula_defensoria')}
                />
              </div>

              <div className="col-md-4 mt-3">
                <label htmlFor="defensor_categoria" className="form-label">Categoria:</label>
                <select
                  id="defensor_categoria"
                  className="form-select"
                  disabled={camposDesabilitados}
                  {...methods.register('defensor.categoria')}
                >
                  <option value="">Selecione...</option>
                  {CategoriaDefensorList.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-8 mt-3">
                <label htmlFor="defensor_anotacao" className="form-label">Anotações:</label>
                <input
                  id="defensor_anotacao"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('defensor.anotacao')}
                />
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="defensor_data_admissao" className="form-label">Admissão:</label>
                  <input
                    id="defensor_data_admissao"
                    type="date"
                    className="form-control"
                    disabled={camposDesabilitados}
                    {...methods.register('defensor.data_admissao')}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="defensor_data_referencia" className="form-label">Referência:</label>
                  <input
                    id="defensor_data_referencia"
                    type="date"
                    className="form-control"
                    disabled={camposDesabilitados}
                    {...methods.register('defensor.data_referencia')}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="defensor_data_exoneracao" className="form-label">Exoneração:</label>
                  <input
                    id="defensor_data_exoneracao"
                    type="date"
                    className="form-control"
                    disabled={camposDesabilitados}
                    {...methods.register('defensor.data_exoneracao')}
                  />
                </div>
              </div>
            </div>
          </div>

          {lotacoesAtuais.length > 0 && (
            <div className="mt-4">
              <table className="table table-sm table-bordered table-hover table-striped">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th className="text-center">Início</th>
                    <th className="text-center">Término</th>
                    <th>Local</th>
                  </tr>
                </thead>
                <tbody>
                  {lotacoesAtuais.map((lotacao, index) => {
                    const local = lotacao.defensoria?.nome || lotacao.nucleo?.nome || lotacao.setor?.nome || '-';
                    return (
                      <tr key={index}>
                        <td>{TipoLotacaoList.find((a) => a.value === lotacao.tipo)?.name}</td>
                        <td className="text-center">{lotacao.data_inicial ? DataAmigavel(lotacao.data_inicial) : '-'}</td>
                        <td className="text-center">{lotacao.data_final ? DataAmigavel(lotacao.data_final) : '-'}</td>
                        <td>
                          {lotacao.id ? (
                            <Link to={`${Links.Vinculos_Editar}/${lotacao.id}`} title="Editar lotação">
                              {local}
                            </Link>
                          ) : (
                            local
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

